// angular
import { Component, input } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
// pipes
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { ImageSourcePipe } from '@app/shared/pipes/image-source.pipe';
// interfaces
import {
  IPeopleProfile,
  PeopleFilterType,
} from '@app/core/shared/interfaces/solutions-profile.interface';
import { PEOPLE_PROFILE_LOGO_PLACEHOLDER } from '@app/shared/constants/people-profile-logo-placeholders.const';
// utils
import {
  profileDisplayAddress,
  profileDisplayBio,
  profileDisplayEmail,
  profileDisplayPhone,
  profileDisplayRegion,
  profileDisplayWebsite,
  profileHasAddressSection,
  profileHasBioSection,
  profileHasContactSection,
  profileHasRealPhone,
  profileHasRegionSection,
  profileRealFirstName,
  profileTypeTranslationKey,
  shouldBlurProfileField,
} from '@app/+solutions/shared/utils/solutions-profile-display.util';

@Component({
  selector: 'pwj-developer-profile-card',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    TranslateModule,
    TranslateDataPipe,
    ImageSourcePipe,
  ],
  templateUrl: './developer-profile-card.component.html',
  styleUrl: './developer-profile-card.component.scss',
})
export class DeveloperProfileCardComponent {
  readonly logoPlaceholder = PEOPLE_PROFILE_LOGO_PLACEHOLDER.PROJECT_OWNER;

  profile = input.required<IPeopleProfile>();
  canViewDetails = input(false);
  cardType = input<PeopleFilterType>('PROJECT_OWNER');

  isLocked(): boolean {
    return !this.canViewDetails();
  }

  isRegionLocked(): boolean {
    return shouldBlurProfileField(
      this.canViewDetails(),
      !!this.profile().operating_city_region?.trim()
    );
  }

  isBioLocked(realBio: string): boolean {
    return shouldBlurProfileField(this.canViewDetails(), !!realBio.trim());
  }

  isAddressLocked(realAddress: string): boolean {
    return shouldBlurProfileField(this.canViewDetails(), !!realAddress.trim());
  }

  isWebsiteLocked(realWebsite: string): boolean {
    return shouldBlurProfileField(this.canViewDetails(), !!realWebsite?.trim());
  }

  isEmailLocked(): boolean {
    return shouldBlurProfileField(
      this.canViewDetails(),
      !!this.profile().contact_email?.trim()
    );
  }

  isPhoneLocked(): boolean {
    return shouldBlurProfileField(
      this.canViewDetails(),
      profileHasRealPhone(this.profile())
    );
  }

  typeTranslationKey(): string {
    return profileTypeTranslationKey(this.cardType());
  }

  showFirstName(): boolean {
    return !!profileRealFirstName(this.profile());
  }

  firstName(): string {
    return profileRealFirstName(this.profile());
  }

  showBio(realBio: string): boolean {
    return profileHasBioSection(this.profile(), this.canViewDetails(), realBio);
  }

  bio(realBio: string): string {
    return profileDisplayBio(this.profile(), this.canViewDetails(), realBio);
  }

  showRegion(): boolean {
    return profileHasRegionSection(this.profile(), this.canViewDetails());
  }

  region(): string {
    return profileDisplayRegion(this.profile(), this.canViewDetails());
  }

  showAddress(realAddress: string): boolean {
    return profileHasAddressSection(
      this.profile(),
      this.canViewDetails(),
      realAddress
    );
  }

  address(realAddress: string): string {
    return profileDisplayAddress(
      this.profile(),
      this.canViewDetails(),
      realAddress
    );
  }

  showWebsite(realWebsite: string): boolean {
    if (this.canViewDetails()) {
      return !!realWebsite?.trim();
    }
    if (realWebsite?.trim()) {
      return true;
    }
    return true;
  }

  website(realWebsite: string): string {
    if (this.canViewDetails() || realWebsite?.trim()) {
      return realWebsite;
    }
    return profileDisplayWebsite(false);
  }

  showContact(): boolean {
    return profileHasContactSection(this.profile(), this.canViewDetails());
  }

  email(): string {
    return profileDisplayEmail(this.profile(), this.canViewDetails());
  }

  phone(): string {
    return profileDisplayPhone(this.profile(), this.canViewDetails());
  }
}
